import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "src/components/app/Button";
import Layout from "src/components/app/layout/Layout";
import CGMClearRestartBottomSheet from "src/components/app/bottom-sheet/cgm/CGMClearRestartBottomSheet";
import {
  BluetoothStep,
  CGMJourneyHeader,
  CGMJourneyStepper,
  DeviceReadyStep,
  BindingStep,
  ErrorStep,
  IntroStep,
  QrStep,
  ScanningStep,
  PlaceSensorStep,
  PlaceTransmitterStep,
} from "src/components/app/cgm/steps";

import { sendMessageToParent } from "src/helpers/common";
import storage from "src/helpers/storage";
import { cgmManager } from "src/services/cgm/cgmManager";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  setStep,
  clearScannedDevices,
  resetCgm,
} from "src/redux/slices/cgmSlice";
import type { CgmDeviceFoundPayload } from "src/types/cgm.types";
import environment from "src/constants/environment";
import { toast } from "sonner";
import { useCgmWebViewMessages } from "src/hooks/useCgmWebViewMessages";

const ContinuousGlucoseMonitor: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cgm = useAppSelector((state) => state.cgm);
  const cgmRef = useRef(cgm);
  cgmRef.current = cgm;
  const patient = useAppSelector((state) => state.auth.patient?.data);

  useCgmWebViewMessages();

  const [showMethodSheet, setShowMethodSheet] = useState(false);
  const [showWarningSheet, setShowWarningSheet] = useState(false);
  const [showGuideSheet, setShowGuideSheet] = useState(false);
  const [showTroubleshootSheet, setShowTroubleshootSheet] = useState(false);
  const [showClearRestartSheet, setShowClearRestartSheet] = useState(false);

  const [bluetoothStatus, setBluetoothStatus] = useState<
    "unknown" | "on" | "off" | "denied"
  >("unknown");

  const journeyLabels = useMemo(
    () => [
      "Before You Start",
      "Provide Permission",
      "Connect Transmitter",
      "Connect Sensor",
      "Put on the sensor",
      "Put on the transmitter",
    ],
    []
  );

  const journeyIndex = useMemo(() => {
    switch (cgm.step) {
      case "device-ready":
        return 0;
      case "bluetooth":
        return 1;
      case "scanning":
        return 2;
      case "qr":
        return 3;
      case "binding":
        return 3;
      case "place-sensor":
        return 4;
      case "place-transmitter":
        return 5;
      default:
        return null;
    }
  }, [cgm.step]);

  // Active warmup / post-bind status lives on cgm-status; redirect there if stored.
  useEffect(() => {
    const storedWarmup = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    console.log(
      "[CGM] mount — storedWarmup:",
      storedWarmup,
      storedWarmup
        ? `| remainMs: ${new Date(storedWarmup).getTime() - Date.now()}`
        : ""
    );
    if (storedWarmup) {
      const endTime = new Date(storedWarmup).getTime();
      if (endTime > Date.now()) {
        console.log("[CGM] mount — active warmup → cgm-status");
        navigate(`/app/${supportProgramKey}/cgm-status`, { replace: true });
        return;
      }
      console.log("[CGM] mount — stored warmup expired, clearing");
      storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    }
    console.log("[CGM] mount — requesting status from RN");
    cgmManager.refetchCgmStatus(undefined, cgmRef.current);
  }, [navigate, supportProgramKey]);

  const openWebView = useCallback((title: string, url: string) => {
    if (!url) return;
    sendMessageToParent(POST_MESSAGE_EVENTS.NAVIGATE_TO_WEBVIEW, {
      title,
      url,
    });
  }, []);

  const handleClose = () => {
    navigate(-1);
  };

  const handleChatClick = () => {
    navigate(`/app/${supportProgramKey}/cgm-chat-with-experts`);
  };

  const handleContinue = () => {
    console.log("[CGM] continue — step:", cgm.step, "| bt:", bluetoothStatus);
    if (cgm.step === "intro") {
      setShowMethodSheet(true);
    } else if (cgm.step === "device-ready") {
      dispatch(setStep("bluetooth"));
    } else if (cgm.step === "bluetooth" && bluetoothStatus === "on") {
      dispatch(setStep("scanning"));
      dispatch(clearScannedDevices());
      sendMessageToParent(POST_MESSAGE_EVENTS.START_CGM_PAIRING, {});
    } else if (cgm.step === "place-sensor") {
      dispatch(setStep("place-transmitter"));
    } else if (cgm.step === "place-transmitter") {
      const warmupEnd = cgm.warmupEndDate;
      const warmupIsActive =
        !!warmupEnd && new Date(warmupEnd).getTime() > Date.now();
      dispatch(setStep(warmupIsActive ? "warmup" : "connected"));
      navigate(`/app/${supportProgramKey}/cgm-status`, { replace: true });
    }
  };

  const handleSelectVirtualInstallation = () => {
    setShowMethodSheet(false);
    const url =
      (environment as any)?.VIRTUAL_INSTALLATION_CGM_URL ||
      (process.env.REACT_APP_VIRTUAL_INSTALLATION_CGM_URL as
        | string
        | undefined);
    if (!url) {
      toast.error("Virtual installation is not available right now.");
      return;
    }
    const patientId =
      (patient as any)?.patient_id ??
      (patient as any)?.patientID ??
      (patient as any)?.patientId ??
      "";
    const finalUrl = patientId ? `${url}?utm_source=${patientId}` : url;
    openWebView("Virtual Installation", finalUrl);
  };

  const handleSelectSelfInstallation = () => {
    setShowMethodSheet(false);
    setShowWarningSheet(true);
  };

  const handleProceedFromWarning = () => {
    setShowWarningSheet(false);
    dispatch(setStep("device-ready"));
  };

  const handleSelectDevice = (device: CgmDeviceFoundPayload) => {
    console.log(
      "[CGM] device selected:",
      device.pocDevice?.name,
      device.pocDevice?.address
    );
    dispatch(setStep("qr"));
    sendMessageToParent(POST_MESSAGE_EVENTS.OPEN_CGM_QR_SCANNER, {
      pocDevice: device.pocDevice,
    });
  };

  const handleRetry = () => {
    console.log("[CGM] retry — resetting state");
    dispatch(resetCgm());
  };

  const handleClearAndRestart = () => {
    console.log("[CGM] clear & restart — unbinding device");
    cgmManager.clearAndRestartConnection();
  };

  const renderFooter = () => {
    if (cgm.step === "binding" || cgm.step === "scanning" || cgm.step === "qr")
      return null;

    if (cgm.step === "intro")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleContinue}
            variant="contained"
            rounded="full"
            fullWidth
          >
            Connect Your CGM
          </Button>
        </div>
      );

    if (cgm.step === "bluetooth") return null;

    if (cgm.step === "error")
      return (
        <div className="space-y-3 p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleRetry}
            variant="contained"
            rounded="full"
            fullWidth
          >
            Try Again
          </Button>
          <Button
            onClick={handleClose}
            variant="outlined"
            rounded="full"
            fullWidth
          >
            Cancel
          </Button>
        </div>
      );

    return null;
  };

  {
    /* ─── INTRO STEP ─── */
  }
  if (cgm.step === "intro") {
    return (
      <IntroStep
        showMethodSheet={showMethodSheet}
        setShowMethodSheet={setShowMethodSheet}
        showWarningSheet={showWarningSheet}
        setShowWarningSheet={setShowWarningSheet}
        showGuideSheet={showGuideSheet}
        setShowGuideSheet={setShowGuideSheet}
        onSelectSelfInstallation={handleSelectSelfInstallation}
        onSelectVirtualInstallation={handleSelectVirtualInstallation}
        onProceedFromWarning={handleProceedFromWarning}
        handleBack={handleClose}
        onStartJourney={handleContinue}
      />
    );
  }

  const renderHeader = () => {
    return (
      <div className="w-full border-b border-gray-200 p-4 space-y-4">
        <CGMJourneyHeader onClose={handleClose} onChatClick={handleChatClick} />
        <CGMJourneyStepper
          labels={journeyLabels}
          currentIndex={journeyIndex ?? 0}
        />
      </div>
    );
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey}
      header={renderHeader()}
      footer={renderFooter()}
      className="bg-white"
      containerClassName="bg-white"
    >
      <div className="relative flex-1">
        {/* ─── DEVICE READY STEP ─── */}
        {cgm.step === "device-ready" && (
          <DeviceReadyStep onContinue={handleContinue} />
        )}

        {/* ─── BLUETOOTH STEP ─── */}
        {cgm.step === "bluetooth" && (
          <BluetoothStep
            onStatusChange={setBluetoothStatus}
            onProceed={handleContinue}
          />
        )}

        {/* ─── SCANNING STEP ─── */}
        {cgm.step === "scanning" && (
          <ScanningStep
            scannedDevices={cgm.scannedDevices}
            onSelectDevice={handleSelectDevice}
          />
        )}

        {/* ─── QR SCAN STEP ─── */}
        {cgm.step === "qr" && <QrStep />}

        {/* ─── BINDING STEP ─── */}
        {cgm.step === "binding" && <BindingStep />}

        {/* PLACE SENSOR STEP */}
        {cgm.step === "place-sensor" && (
          <PlaceSensorStep onContinue={handleContinue} />
        )}

        {/* ─── PLACE TRANSMITTER STEP ─── */}
        {cgm.step === "place-transmitter" && (
          <PlaceTransmitterStep onContinue={handleContinue} />
        )}

        {/* ─── ERROR STEP ─── */}
        {cgm.step === "error" && <ErrorStep error={cgm.error} />}
      </div>

      <CGMClearRestartBottomSheet
        show={showClearRestartSheet}
        onCancel={() => setShowClearRestartSheet(false)}
        onConfirm={() => {
          setShowClearRestartSheet(false);
          handleClearAndRestart();
        }}
      />
    </Layout>
  );
};

export default ContinuousGlucoseMonitor;
